import { useState, useEffect } from "react";

function useCountdown(hours: number) {
  const getInitial = () => {
    const stored = localStorage.getItem("negoia_deadline");
    if (stored) {
      const diff = parseInt(stored) - Date.now();
      if (diff > 0) return diff;
    }
    const deadline = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem("negoia_deadline", deadline.toString());
    return hours * 60 * 60 * 1000;
  };

  const [ms, setMs] = useState(getInitial);

  useEffect(() => {
    const interval = setInterval(() => {
      setMs((prev) => {
        if (prev <= 1000) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const totalSecs = Math.floor(ms / 1000);
  const h = Math.floor(totalSecs / 3600).toString().padStart(2, "0");
  const m = Math.floor((totalSecs % 3600) / 60).toString().padStart(2, "0");
  const s = (totalSecs % 60).toString().padStart(2, "0");
  return { h, m, s };
}

const bonuses = [
  {
    icon: "ri-refresh-line",
    color: "from-emerald-600 to-emerald-500",
    glow: "bg-emerald-500/20",
    border: "border-emerald-500/30",
    tag: "BONO #1",
    title: "Actualizaciones Mensuales",
    desc: "Cada mes recibirás nuevas herramientas, scripts y plantillas actualizadas con las últimas tendencias del mercado peruano. Tu acceso siempre al día.",
    value: "$47",
  },
  {
    icon: "ri-group-fill",
    color: "from-[#7C3AED] to-[#6d28d9]",
    glow: "bg-[#7C3AED]/20",
    border: "border-[#7C3AED]/30",
    tag: "BONO #2",
    title: "Grupo de Soporte Exclusivo",
    desc: "Accede a nuestra comunidad privada de emprendedores peruanos. Resuelve dudas, comparte resultados y crece junto a una red de negocios reales.",
    value: "$37",
  },
];

export default function FinalCTA() {
  const { h, m, s } = useCountdown(24);

  return (
    <section id="cta" className="relative overflow-hidden py-16 md:py-28">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0B0F19]" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/40 via-[#0B0F19] to-orange-900/30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[#7C3AED]/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-orange-500/15 rounded-full blur-[100px] pointer-events-none" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
        {/* Urgency badge */}
        <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 text-orange-300 text-xs font-bold px-4 py-2 rounded-full mb-6 animate-pulse">
          <i className="ri-time-line"></i>
          Precio de lanzamiento — Solo por tiempo limitado
        </div>

        {/* Countdown */}
        <div className="mb-8">
          <p className="text-white/60 text-sm mb-3 uppercase tracking-widest font-semibold">
            Este precio de $12 estará disponible solo por:
          </p>
          <div className="flex items-center justify-center gap-2 md:gap-3">
            {[{ label: "Horas", val: h }, { label: "Minutos", val: m }, { label: "Segundos", val: s }].map((unit, i) => (
              <>
                <div key={unit.label} className="flex flex-col items-center">
                  <div className="bg-white/10 border border-white/20 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[64px] md:min-w-[80px]">
                    <span className="text-3xl md:text-4xl font-black text-white tabular-nums">{unit.val}</span>
                  </div>
                  <span className="text-white/40 text-xs mt-1.5 uppercase tracking-wider">{unit.label}</span>
                </div>
                {i < 2 && (
                  <span key={`sep-${i}`} className="text-white/50 text-3xl font-black mb-4">:</span>
                )}
              </>
            ))}
          </div>
          <p className="text-orange-400/80 text-xs mt-3 font-semibold">
            ⚠ Cuando el contador llegue a cero, el precio subirá
          </p>
        </div>

        {/* Main headline */}
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-6">
          El mercado no perdona{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
            a los lentos.
          </span>
          <br />
          <span className="text-white/80 text-2xl md:text-4xl lg:text-5xl">
            ¿Te subes o te quedas atrás?
          </span>
        </h2>

        <p className="text-white/60 text-sm md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          Mientras lees esto, tu competencia ya está usando herramientas de IA para quitarte clientes.
          El momento de actuar es{" "}
          <strong className="text-white">ahora</strong>.
        </p>

        {/* BONOS */}
        <div className="mb-10">
          <p className="text-[#a78bfa] text-sm font-black uppercase tracking-widest mb-5">
            🎁 Bonos incluidos — Gratis al comprar hoy
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {bonuses.map((b, i) => (
              <div key={i} className="relative group text-left">
                <div className={`absolute inset-0 ${b.glow} rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500`} />
                <div className={`relative bg-white/5 border ${b.border} rounded-2xl p-5 transition-all duration-300 group-hover:-translate-y-0.5`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-black px-2.5 py-1 rounded-full bg-gradient-to-r ${b.color} text-white`}>
                      {b.tag}
                    </span>
                    <span className="text-white/30 text-xs line-through">Valor: {b.value}</span>
                  </div>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${b.color} mb-3`}>
                    <i className={`${b.icon} text-white text-xl`}></i>
                  </div>
                  <h4 className="text-white font-black text-base mb-1.5">{b.title}</h4>
                  <p className="text-white/50 text-xs leading-relaxed">{b.desc}</p>
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-400 text-sm"></i>
                    </div>
                    <span className="text-emerald-400 text-xs font-semibold">Incluido GRATIS</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price display */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl px-8 py-6 text-center">
            <p className="text-white/40 text-sm mb-1">Valor total del paquete</p>
            <p className="text-white/30 text-2xl font-black line-through mb-2">$97</p>
            <p className="text-[#a78bfa] text-xs font-bold uppercase tracking-widest mb-1">Precio especial de hoy</p>
            <p className="text-6xl md:text-7xl font-black text-white">$12</p>
            <p className="text-white/40 text-xs md:text-sm mt-2">Pago único. Sin mensualidades. Sin sorpresas.</p>
          </div>
        </div>

        {/* Device compatibility */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
          {[
            { icon: "ri-smartphone-line", text: "iOS & Android" },
            { icon: "ri-tablet-line", text: "Tablet" },
            { icon: "ri-computer-line", text: "PC / Mac" },
            { icon: "ri-lock-password-line", text: "Acceso Privado" },
          ].map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-white/8 border border-white/15 px-3 py-1.5 rounded-full">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${d.icon} text-[#a78bfa] text-sm`}></i>
              </div>
              <span className="text-white/70 text-xs font-semibold">{d.text}</span>
            </div>
          ))}
        </div>

        {/* Private access info */}
        <div className="bg-white/5 border border-[#7C3AED]/30 rounded-xl px-4 py-3 mb-8 max-w-sm mx-auto">
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-lock-fill text-[#a78bfa] text-base"></i>
            </div>
            <p className="text-white/70 text-xs md:text-sm">
              Acceso <strong className="text-white">100% privado</strong> con usuario y contraseña personal
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex flex-col items-center gap-3">
          <a
            href="https://pay.hotmart.com/B105486556C?checkoutMode=10"
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 hover:from-yellow-400 hover:via-orange-400 hover:to-orange-500 text-white font-black text-lg md:text-2xl px-10 md:px-16 py-5 md:py-7 rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/60 cursor-pointer group w-full sm:w-auto max-w-sm sm:max-w-none whitespace-nowrap overflow-hidden"
          >
            {/* Shimmer effect */}
            <span className="absolute inset-0 -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 bg-white/20 w-1/2" />
            <i className="ri-flashlight-line text-2xl group-hover:scale-125 transition-transform duration-300"></i>
            QUIERO MIS VENTAS HOY — ENTRAR POR $12
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
          </a>

          <p className="text-white/35 text-xs md:text-sm text-center">
            ⚡ Acceso inmediato &bull; Pago único sin mensualidades &bull; Garantía 7 días
          </p>
        </div>

        {/* Security badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 md:gap-8">
          {[
            { icon: "ri-shield-check-fill", text: "SSL Seguro" },
            { icon: "ri-bank-card-fill", text: "Visa / Mastercard" },
            { icon: "ri-refresh-line", text: "Garantía 7 días" },
            { icon: "ri-customer-service-2-fill", text: "Soporte 24/7" },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 text-white/40">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${b.icon} text-emerald-400 text-sm`}></i>
              </div>
              <span className="text-xs font-medium">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
